import { Wallet, Copy, Check, LogOut, ExternalLink, Rocket } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useWallet } from "@/contexts/WalletContext";
import AirdropSignup from "@/sections/AirdropSignup";

const SHORT = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : "");

function CopyAddr({ addr, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(addr);
        setCopied(true);
        toast.success("Address copied");
        setTimeout(() => setCopied(false), 1400);
      }}
      data-testid={testid}
      className="flex items-center gap-2 text-[#cfff04] hover:underline font-mono text-xs"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />} {SHORT(addr)}
    </button>
  );
}

function PhantomCard() {
  const { phantom, connectPhantom, disconnectPhantom } = useWallet();
  const { address, balance, connecting } = phantom;
  return (
    <div className="border border-[#3a3a3a] bg-[#121212] p-6" data-testid="wallet-phantom">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <span className="w-3 h-3" style={{ background: "#AB9FF2" }} />
          <span className="font-display text-base font-bold uppercase tracking-tight">Phantom</span>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">Solana</span>
      </div>

      {!address ? (
        <>
          <p className="text-sm text-[#e2e2e2] mb-5">
            Connect your Solana wallet to mint your coin as a real SPL token from the address you trust — and to receive tips from fans.
          </p>
          <button
            onClick={connectPhantom}
            disabled={connecting}
            data-testid="phantom-connect"
            className="w-full btn-brutal bg-[#AB9FF2] text-[#050505] font-bold uppercase tracking-wider text-sm py-3 flex items-center justify-center gap-2"
          >
            <Wallet size={14} /> {connecting ? "Connecting…" : "Connect Phantom"}
          </button>
        </>
      ) : (
        <>
          <div className="space-y-3 mb-5 font-mono text-xs">
            <div className="flex items-center justify-between">
              <span className="text-[#d0d0d0] uppercase tracking-wider">Address</span>
              <CopyAddr addr={address} testid="phantom-copy" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[#d0d0d0] uppercase tracking-wider">Balance</span>
              <span className="text-white">{balance === null ? "—" : `${balance.toFixed(4)} SOL`}</span>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-[#333]">
              <span className="text-[#d0d0d0] uppercase tracking-wider">Network</span>
              <span className="text-[#00ff41]">Mainnet-beta</span>
            </div>
          </div>

          <a
            href={`https://solscan.io/account/${address}`}
            target="_blank"
            rel="noreferrer"
            className="w-full border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#e2e2e2] py-2.5 font-mono text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 mb-2"
            data-testid="phantom-explorer"
          >
            View on Solscan <ExternalLink size={11} />
          </a>
          <button
            onClick={disconnectPhantom}
            data-testid="phantom-disconnect"
            className="w-full border border-[#3a3a3a] hover:border-[#ff3333] hover:text-[#ff3333] text-[#d0d0d0] py-2 font-mono text-[11px] uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <LogOut size={11} /> Disconnect
          </button>
        </>
      )}
    </div>
  );
}

function MintCard() {
  const { anyConnected } = useWallet();
  return (
    <div className="border border-[#cfff04] bg-[#121212] p-6" data-testid="wallet-mint-card">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-3">
        // In-app minting
      </div>
      <div className="font-display text-xl font-bold uppercase mb-4">
        {anyConnected ? "Ready to mint." : "Mint without the terminal."}
      </div>
      <p className="text-sm text-[#e2e2e2] mb-5">
        Publish your coin, then mint it as a real SPL token directly from your coin page — Phantom
        signs, we never touch your keys. Free devnet test run included, mainnet when you're ready.
      </p>
      <ul className="space-y-2 mb-6 text-sm text-[#e2e2e2]">
        <li className="flex items-center gap-2">
          <Check size={12} className="text-[#00ff41]" /> 1B supply · 9 decimals
        </li>
        <li className="flex items-center gap-2">
          <Check size={12} className="text-[#00ff41]" /> Mint + freeze authority revoked (RugCheck friendly)
        </li>
        <li className="flex items-center gap-2">
          <Check size={12} className="text-[#00ff41]" /> All signing happens in your browser
        </li>
      </ul>
      <Link
        to="/create"
        data-testid="wallet-mint-cta"
        className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-5 py-3 inline-flex items-center gap-2"
      >
        <Rocket size={14} /> Launch a Coin
      </Link>
    </div>
  );
}

export default function WalletConnect() {
  return (
    <section id="wallet" className="border-t border-[#3a3a3a]" data-testid="section-wallet">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
              05 // Connect Wallet
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tighter">
              Connect your wallet.
              <br />
              Mint <span className="text-[#cfff04]">for real.</span>
            </h2>
          </div>
          <p className="col-span-12 lg:col-span-4 self-end text-[#e2e2e2] text-sm">
            Phantom for Solana. We never see your keys — connection is fully client-side. Mock
            trading above shows how the loop feels. Minting turns it real.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-3 mb-3">
          <div className="col-span-12 md:col-span-5">
            <PhantomCard />
          </div>
          <div className="col-span-12 md:col-span-7">
            <MintCard />
          </div>
        </div>

        <AirdropSignup />
      </div>
    </section>
  );
}
